import { html, nothing } from 'lit';
import { map } from 'lit/directives/map.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { BoilerplateModal } from './boilerplate_modal';

class ModalWishlistShare extends BoilerplateModal {
  static get properties() {
    return {
      props: { type: Object, attribute: true },
      shareUrl: { type: String },
      products: { type: Array, state: true },
      allProducts: { type: Array, state: true },
    };
  }

  async connectedCallback() {
    super.connectedCallback();

    await this.getProducts();

    window.eventKeeper?.onEventHeard('wishlist:initialise', async (detail) => {
      await this.getProducts();
    });

    document.addEventListener('wishlist:initialise', async () => {
      await this.getProducts();
    });

    document.addEventListener('wishlist:remove', async () => {
      await this.getProducts();
    });

    document.addEventListener('wishlist:update', async () => {
      await this.getProducts();
    });
  }

  async disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('wishlist:initialise', async () => {
      await this.getProducts();
    });

    document.removeEventListener('wishlist:remove', async () => {
      await this.getProducts();
    });

    document.removeEventListener('wishlist:update', async () => {
      await this.getProducts();
    });
  }

  update(changedProperties) {
    super.update();

    if (changedProperties.has('detail')) {
      this.getProducts();
    }
  }

  async getProducts() {
    this.allProducts = await window.wishlist.getProducts();
    const wishlistObj = await window.wishlist.retrieve();

    if (!this.detail) {
      return [];
    }

    const currentBoard = wishlistObj.boards.find(
      (board) => board.id === this.detail?.boardId
    );
    if (!currentBoard) {
      return [];
    }

    const currentBoardsItems = currentBoard.items.map((item) => {
      const { productId } = window.wishlist.parseWishlistId(item);
      return productId;
    });

    const set = new Set(currentBoardsItems);
    this.products = this.allProducts.filter((product) =>
      set.has(product.itemId)
    );
  }

  async handleMoreClick() {
    if (!typeof window.navigator.share === 'function' || !this.detail) {
      return;
    }

    try {
      await window.navigator.share({
        title: window.theme.strings.wishlist_share.share_title,
        text: window.theme.strings.wishlist_share.share_text,
        url: this.detail?.url,
      });
    } catch (error) {
      console.error('Error sharing wishlist:', error);
    }
  }

  renderBody() {
    return html`
      <div class="subtitle">
        ${unsafeHTML(window.theme.strings.wishlist_share.description)}
      </div>
      <div class="share_description">
        ${unsafeHTML(window.theme.strings.wishlist_share.share_description)}
      </div>
      <div class="link_sharer_container">
        <link-sharer url=${this.detail?.url}></link-sharer>
      </div>
      <div class="social_sharing">
        <div class="social_title">
          ${unsafeHTML(window.theme.strings.wishlist_share.share_directly_to)}
        </div>
        <div class="social_buttons">
          ${map(
            this.props.socials,
            (social) => html`
              <a
                href="${social.link.replace(
                  'SHARE_URL_PLACEHOLDER',
                  encodeURIComponent(this.detail?.url)
                )}"
                aria-label="Share to ${social.name}"
              >
                <i class="${social.icon} social-icon"></i>
              </a>
            `
          )}
          ${typeof window.navigator.share === 'function'
            ? html`<button
                @click=${this.handleMoreClick}
                type="button"
                aria-label="More Social options"
                class="more_socials"
                aria-haspopup="dialog"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                    stroke="black"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z"
                    stroke="black"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z"
                    stroke="black"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>`
            : nothing}
        </div>
      </div>
      ${this.products && this.products.length > 0 ? html`
      <div class="preview_title">${unsafeHTML(window.theme.strings.wishlist_share.items)}</div>
      ${this.products && this.products.length > 0
        ? html`<ul class="preview_items">
            ${map(
              this.products.reverse().slice(0, 3),
              (item) => html`
                <li class="preview_item">
                  <picture>
                    <img
                      src=${item.product.featured_image}
                      alt=${item.product.title}
                      loading="lazy"
                      width="75"
                      height="auto"
                      sizes="(max-width: 768px) 75px, 75px"
                    />
                  </picture>
                </li>
              `
            )}
            ${this.products.length > 3
              ? html`<li class="preview_more">+${this.products.length - 3}</li>`
              : nothing}
          </ul>`
        : nothing}
    ` : nothing}
   `;
  }
}

window.customElements.get('modal-wishlist-share') ||
  window.customElements.define('modal-wishlist-share', ModalWishlistShare);
